import React from "react";
import { motion, AnimatePresence } from "framer-motion";

interface OverlaySuccessCardProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    overlay: {
        id: string | number;
        name: string;
        image_url: string;
        background: string;
        basefeathers: string;
        headaccessories: string;
        eyes: string;
        accessories: string;
        mouth: string;
        // Optional QR code URLs if needed:
        solanaQrUrl?: string;
        twitterQrUrl?: string;
    };
}

const OverlaySuccessCard: React.FC<OverlaySuccessCardProps> = ({ open, onOpenChange, overlay }) => (
    <AnimatePresence>
        {open && (
            <>
                {/* Backdrop */}
                <motion.div
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => onOpenChange(false)}
                />
                
                {/* Modal Content */}
                <motion.div
                    className="fixed top-1/2 left-1/2 w-[90vw] max-w-[480px] bg-[#121212] text-white rounded-xl p-6 shadow-2xl z-50 -translate-x-1/2 -translate-y-1/2 border border-white/10"
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                >
                    {/* Red Stamp of Authenticity */}
                    <div className="absolute bottom-4 right-4 z-10 pointer-events-none">
                        <AuthenticityStamp />
                    </div>

                    {/* Close Button */}
                    <motion.button
                        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                        onClick={() => onOpenChange(false)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </motion.button>

                    {/* Top Title Row */}
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-4 flex justify-between items-center relative"
                    >
                        <h2 className="text-2xl font-bold">BOOBIES</h2>
                        <span className="text-brand-cyan font-bold text-xl absolute -right-10 top-[30%] rotate-90 whitespace-nowrap origin-left">
                            {overlay.name}
                        </span>
                    </motion.div>

                    {/* Image with yellow border */}
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="flex justify-center mb-4"
                    >
                        <div className="border-[3px] border-[#FFD600] w-[265px] h-[265px] flex items-center justify-center bg-black">
                            <img
                                src={overlay.image_url}
                                alt={overlay.name}
                                className="w-[260px] h-[260px] object-contain block"
                            />
                        </div>
                    </motion.div>

                    {/* Name and underline */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="mb-4"
                    >
                        <h3 className="text-xl font-bold mb-2">{overlay.name}</h3>
                        <div className="h-px bg-[#333] w-full" />
                    </motion.div>

                    {/* Attributes Grid */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex flex-col gap-2 mb-6"
                    >
                        {[
                            ['BACKGROUND', overlay.background],
                            ['BASEFEATHERS', overlay.basefeathers],
                            ['HEADACCESSORIES', overlay.headaccessories],
                            ['EYES', overlay.eyes],
                            ['ACCESSORIES', overlay.accessories],
                            ['MOUTH', overlay.mouth],
                        ].map(([label, value], index) => (
                            <motion.div
                                key={label}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6 + index * 0.1 }}
                                className="flex items-center justify-between text-sm"
                            >
                                <span className="text-gray-400 font-bold w-[40%]">{label}</span>
                                <span className="flex-1 text-right truncate">{value}</span>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* QR Codes Row */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2 }}
                        className="flex justify-center gap-12 mb-6"
                    >
                        {overlay.solanaQrUrl && (
                            <div className="text-center">
                                <img src={overlay.solanaQrUrl} alt="Solana QR" className="w-[100px] h-[100px]" />
                                <p className="text-xs mt-1">SOLANA.FM</p>
                            </div>
                        )}
                        {overlay.twitterQrUrl && (
                            <div className="text-center">
                                <img src={overlay.twitterQrUrl} alt="Twitter QR" className="w-[100px] h-[100px]" />
                                <p className="text-xs mt-1">X @PolimeniJoe</p>
                            </div>
                        )}
                    </motion.div>

                    {/* Footer text */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4 }}
                    >
                        <p className="text-xs text-gray-400 text-center">
                            BOOBIES is an exclusive generative art collection on the Solana blockchain.
                        </p>
                    </motion.div>
                </motion.div>
            </>
        )}
    </AnimatePresence>
);

export default OverlaySuccessCard;


const AuthenticityStamp = () => (
    <motion.svg
        width={110}
        height={110}
        viewBox="0 0 100 100"
        initial={{ scale: 0, rotate: -30, opacity: 0 }}
        animate={{ scale: 1, rotate: -15, opacity: 1 }}
        exit={{ scale: 0, rotate: -30, opacity: 0 }}
        transition={{ delay: 0.4, type: "spring" }}
    >
        <g>
            {/* Scalloped border */}
            <circle
                cx="50"
                cy="50"
                r="48"
                fill="none"
                stroke="#00BFFF"
                strokeWidth="5"
                strokeDasharray="3,3"
            />
            {/* Text on banner - two lines */}
            <text
                x="50"
                y="50"
                textAnchor="middle"
                fill="#00BFFF"
                fontWeight="bold"
                fontSize="12"
                transform="rotate(-20 50 54)"
                style={{
                    fontFamily: "monospace, sans-serif",
                    letterSpacing: 1,
                    fontWeight: 900,
                }}
            >
                AUTHENTIC
            </text>
            <text
                x="55"
                y="66"
                textAnchor="middle"
                fill="#00BFFF"
                fontWeight="bold"
                fontSize="13"
                transform="rotate(-20 50 66)"
                style={{
                    fontFamily: "monospace, sans-serif",
                    letterSpacing: 1,
                    fontWeight: 900,
                }}
            >
                BOOBIES
            </text>
        </g>
    </motion.svg>
);
